import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ComputerIcon from '@mui/icons-material/Computer';
import CodeIcon from '@mui/icons-material/Code';
import BarChartIcon from '@mui/icons-material/BarChart';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import CloudIcon from '@mui/icons-material/Cloud';
import SchoolIcon from '@mui/icons-material/School';
import SecurityIcon from '@mui/icons-material/Security';
import { SERVICES_LIST } from '../../utils/constants';

const serviceIcons: { [key: string]: React.ReactNode } = {
  conseil: <ComputerIcon sx={{ fontSize: 40 }} />,
  developpement: <CodeIcon sx={{ fontSize: 40 }} />,
  bi: <BarChartIcon sx={{ fontSize: 40 }} />,
  data: <DataUsageIcon sx={{ fontSize: 40 }} />,
  cloud: <CloudIcon sx={{ fontSize: 40 }} />,
  formation: <SchoolIcon sx={{ fontSize: 40 }} />,
  cybersecurite: <SecurityIcon sx={{ fontSize: 40 }} />,
};

const ServicesSection: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      id="services"
      sx={{
        py: 10,
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            Nos Services
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}
          >
            Des solutions sur mesure pour chaque besoin de votre entreprise
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {SERVICES_LIST.map((service, index) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={service.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{ height: '100%' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'visible',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      borderRadius: '8px 8px 0 0',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                        color: 'primary.main',
                        mb: 2,
                      }}
                    >
                      {serviceIcons[service.id]}
                    </Box>

                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                      {service.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {service.description}
                    </Typography>

                    <List dense>
                      {service.features.map((feature, idx) => (
                        <ListItem key={idx} disableGutters>
                          <ListItemIcon sx={{ minWidth: 35 }}>
                            <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{
                              variant: 'body2',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ServicesSection;